import * as v from 'valibot'
import type { LevelBadge } from './levels'

/**
 * Accounts and the approval gate.
 *
 * Membership is closed: there is no signup path. An administrator whitelists
 * an email, which creates the account; until then the email simply does not
 * exist here. A whitelisted person signs in with Google (their address is the
 * thing that was whitelisted, so the provider verifies ownership of it), then
 * fills in the profile the company needs, then waits while an administrator
 * reviews it. Access to the workflow opens only after that approval.
 *
 * The status lifecycle, and the one rule each transition follows:
 *
 *   invited   allowlisted, never signed in or profile not yet submitted
 *   pending   profile submitted, waiting for an administrator
 *   active    approved; the workflow is open to them
 *   suspended blocked for now — a temporary decision, lifted by restoring
 *   revoked   access withdrawn — deliberate, without deleting the account
 *   rejected  a refused request; the door can be re-opened by whitelisting
 *             the address again
 *
 * Every blocked state refuses sign-in and revokes live sessions. No status
 * deletes anything: an account row, its profile and its audit history are
 * permanent, so every decision here is revisitable.
 *
 * Three roles, one column. Admin is the operator; staff run the console;
 * players get the portal. The finer question of who may see which client or
 * job is a separate system and is deliberately not built here.
 */

export const ACCOUNT_ROLES = ['admin', 'staff', 'player'] as const
export type AccountRole = (typeof ACCOUNT_ROLES)[number]

export const ACCOUNT_STATUSES = ['invited', 'pending', 'active', 'suspended', 'revoked', 'rejected'] as const
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number]

export const IDENTITY_TYPES = ['nid', 'passport'] as const
export type IdentityType = (typeof IDENTITY_TYPES)[number]

export const PAYMENT_METHODS = ['bank', 'bKash', 'Nagad', 'custom'] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

/**
 * The clock, not the account: whether the person is taking work right now.
 * The person themselves moves it — an administrator never marks someone
 * Active on their behalf — because Active is a decision to work, the
 * platform's word for clocking in.
 */
export const AVAILABILITY = ['active', 'inactive'] as const
export type Availability = (typeof AVAILABILITY)[number]

/** An account, as any signed-in session sees it. */
export interface Account {
  id: string
  email: string
  /** The display name: the Google profile name until the member submits one. */
  name: string | null
  /**
   * The name the studio knows them by: the Discord display name from their
   * profile, else their Discord username; null until a profile names one.
   * The console shows it first, before `name`.
   */
  discordName?: string | null
  /**
   * The Google profile picture's URL, captured at sign-in. The ops stack
   * carries it; the engine's copy of the account row joins when it adopts
   * the same capture, so the field is optional until then.
   */
  pictureUrl?: string | null
  role: AccountRole
  status: AccountStatus
  /**
   * A player's level — Level 0 from the day they join; null for staff and
   * administrators, who take no work. Optional while the engine's copy of
   * the account row does not carry it.
   */
  level?: LevelBadge | null
  /** The person's own clock: available for new tasks, or not. Defaults to `inactive`. */
  availability: Availability
  /** When the clock was last moved — by the person, never by anyone else. */
  availabilityAt: string | null
  invitedBy: string | null
  createdAt: string
  updatedAt: string
  signedInAt: string | null
}

/**
 * A directory entry: the account, plus the presence its live sessions
 * imply. Presence is derived, not stored — the newest live session's last
 * use — so "online" is the browser having spoken to the API recently, never
 * a second thing for a person to maintain.
 */
export interface DirectoryAccount extends Account {
  /** When any live session of this account last spoke to the API. */
  lastSeenAt: string | null
  /** True when that was within the last few minutes — on the site now. */
  online: boolean
}

/** The profile a member submits once, then keeps current. */
export interface AccountProfile {
  accountId: string
  // Discord
  discordDisplayName: string | null
  discordUsername: string | null
  discordUserId: string | null
  // Personal
  fullName: string | null
  currentAddress: string | null
  dateOfBirth: string | null
  // Contact
  primaryPhone: string | null
  emergencyContactName: string | null
  emergencyContactRelationship: string | null
  emergencyContactNumber: string | null
  // Identity / verification
  identityType: IdentityType | null
  identityNumber: string | null
  // Payment
  paymentMethod: string | null
  paymentAccountNumber: string | null
  paymentBankName: string | null
  paymentDetails: string | null
  submittedAt: string | null
  updatedAt: string | null
}

/** An account with its profile, for review. */
export interface MemberDetail extends Account {
  profile: AccountProfile | null
}

/**
 * The two profile fields the admin surfaces hold behind the mask — the
 * identifiers a leaked screenshot can spend. Everything else on a profile is
 * working data a studio needs in view; these two are only ever needed
 * deliberately, so they are the ones that ask.
 */
export const SENSITIVE_PROFILE_FIELDS = ['identityNumber', 'paymentAccountNumber'] as const
export type SensitiveProfileField = (typeof SENSITIVE_PROFILE_FIELDS)[number]

/**
 * The mask's own face: five middle dots, then the value's last four. Both
 * sides of the wall spell it the same way — the console reads it as "not
 * yours yet", the server treats a patch arriving in it as "no change", so a
 * form can never overwrite a record it never saw.
 */
export const MASKED_PREFIX = '·····'

/** How an administrator asks to see past the mask: the fields, named. */
export const revealProfileSchema = v.object({
  fields: v.pipe(
    v.array(v.picklist(SENSITIVE_PROFILE_FIELDS)),
    v.minLength(1, 'Name at least one field to reveal.'),
  ),
})
export type RevealProfileInput = v.InferOutput<typeof revealProfileSchema>

/** What a reveal answers: the full value of each field that was asked for. */
export type RevealedProfileFields = Partial<Pick<AccountProfile, SensitiveProfileField>>

/**
 * One live session of the account that asked — a row of the devices list.
 * `current` marks the session this very request walked in through, so the
 * list can say "you are here" instead of leaving the reader to count.
 */
export interface AccountSessionView {
  id: string
  ip: string | null
  ua: string | null
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string
  current: boolean
}

/** What `/auth/session` answers: who is signed in, and what sign-in offers. */
export interface AuthState {
  account: Account | null
  terms: {
    version: string
    issuedOn: string
    required: boolean
    acceptedAt: string | null
  }
  /** `google` is true when OAuth credentials are configured on the server. */
  auth: { google: boolean; devSignIn: boolean; googleClientId: string | null }
}

/** The immutable release identity shown to non-administrator invitees. */
export const TEAM_TERMS_VERSION = '1.2'
export const TEAM_TERMS_ISSUED_ON = '2026-10-08'

/** The explicit account-based acceptance action; true is required, never inferred from browsing. */
export const acceptTeamTermsSchema = v.object({
  version: v.pipe(v.string(), v.minLength(1), v.maxLength(20)),
  agree: v.literal(true),
})
export type AcceptTeamTermsInput = v.InferOutput<typeof acceptTeamTermsSchema>

export interface AuditEvent {
  id: string
  actorId: string | null
  actorEmail: string | null
  action: string
  target: string | null
  data: Record<string, unknown> | null
  createdAt: string
}

const required = (max: number) =>
  v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, 'This field is required.'),
    v.maxLength(max, `Use at most ${max} characters.`),
  )
const phone = v.pipe(v.string(), v.trim(), v.regex(/^[+0-9()\-\s]{6,30}$/, 'This is not a valid phone number.'))

/**
 * The profile the company needs, in the order the form asks for it. New
 * requirements are added here and in one migration; nothing else moves.
 */
export const submitProfileSchema = v.pipe(
  v.object({
  // Discord
  discordDisplayName: required(80),
  discordUsername: required(80),
  discordUserId: v.pipe(v.string(), v.trim(), v.regex(/^\d{5,25}$/, 'A Discord user id is digits only.')),
  // Personal
  fullName: required(120),
  currentAddress: required(500),
  dateOfBirth: v.pipe(v.string(), v.trim(), v.isoDate('Enter the date as YYYY-MM-DD.')),
  // Contact
  primaryPhone: phone,
  emergencyContactName: required(120),
  emergencyContactRelationship: required(80),
  emergencyContactNumber: phone,
  // Identity / verification
  identityType: v.picklist(IDENTITY_TYPES),
  identityNumber: required(40),
  // Payment
  paymentMethod: v.picklist(PAYMENT_METHODS, 'Choose a payment method.'),
  paymentAccountNumber: v.pipe(v.string(), v.trim(), v.maxLength(60)),
  paymentBankName: v.pipe(v.string(), v.trim(), v.maxLength(120)),
  paymentDetails: v.pipe(v.string(), v.trim(), v.maxLength(500)),
  }),
  v.forward(
    v.check((input) => input.paymentMethod === 'custom' || input.paymentAccountNumber.length > 0, 'This field is required.'),
    ['paymentAccountNumber'],
  ),
  v.forward(
    v.check((input) => input.paymentMethod !== 'bank' || input.paymentBankName.length > 0, 'This field is required.'),
    ['paymentBankName'],
  ),
  v.forward(
    v.check((input) => input.paymentMethod !== 'custom' || input.paymentDetails.length > 0, 'This field is required.'),
    ['paymentDetails'],
  ),
)

export type SubmitProfileInput = v.InferOutput<typeof submitProfileSchema>

/**
 * The profile form's early pass, in the browser: the same schema the server
 * parses with, asked field by field. Returns one sentence per failing field —
 * "This field is required.", "This is not a valid phone number." — so the
 * form and the API can never disagree about what went wrong or what to call
 * it. A field absent from the answer is a field the server will accept.
 */
export function profileFieldErrors(input: unknown): Partial<Record<string, string>> {
  const result = v.safeParse(submitProfileSchema, input)
  const errors: Partial<Record<string, string>> = {}
  if (result.success) return errors
  for (const issue of result.issues) {
    const path = v.getDotPath(issue)
    if (path && !errors[path]) errors[path] = issue.message
  }
  return errors
}

/**
 * The profile as an administrator may fix it: any subset of the record, plus
 * the display name the account carries. Absent means unchanged. A person can
 * always correct their own record — this is for the typo they cannot see and
 * the name they never filed.
 */
export const adminUpdateProfileSchema = v.pipe(
  v.object({
    name: v.optional(v.union([v.pipe(v.string(), v.trim(), v.minLength(1, 'This field is required.'), v.maxLength(120, 'Use at most 120 characters.')), v.null()])),
    discordDisplayName: v.optional(required(80)),
    discordUsername: v.optional(required(80)),
    discordUserId: v.optional(v.pipe(v.string(), v.trim(), v.regex(/^\d{5,25}$/, 'A Discord user id is digits only.'))),
    fullName: v.optional(required(120)),
    currentAddress: v.optional(required(500)),
    dateOfBirth: v.optional(v.pipe(v.string(), v.trim(), v.isoDate('Enter the date as YYYY-MM-DD.'))),
    primaryPhone: v.optional(phone),
    emergencyContactName: v.optional(required(120)),
    emergencyContactRelationship: v.optional(required(80)),
    emergencyContactNumber: v.optional(phone),
    identityType: v.optional(v.picklist(IDENTITY_TYPES)),
    identityNumber: v.optional(required(40)),
    paymentMethod: v.optional(v.picklist(PAYMENT_METHODS)),
    paymentAccountNumber: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(60))),
    paymentBankName: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(120))),
    paymentDetails: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(500))),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type AdminUpdateProfileInput = v.InferOutput<typeof adminUpdateProfileSchema>

const emailAddress = v.pipe(
  v.string(),
  v.trim(),
  v.toLowerCase(),
  v.email('This is not a valid email address.'),
  v.maxLength(254, 'Use at most 254 characters.'),
)

export const whitelistAccountSchema = v.object({
  email: emailAddress,
  role: v.optional(v.picklist(ACCOUNT_ROLES), 'staff'),
})

export type WhitelistAccountInput = v.InferOutput<typeof whitelistAccountSchema>

export const changeRoleSchema = v.object({ role: v.picklist(ACCOUNT_ROLES) })

/** The one word on the clock only its owner may speak. */
export const setAvailabilitySchema = v.object({ availability: v.picklist(AVAILABILITY) })
export type SetAvailabilityInput = v.InferOutput<typeof setAvailabilitySchema>

export const devSignInSchema = v.object({
  email: emailAddress,
})

/** One Tap's whole request: the signed credential Google put in the browser's hand. */
export const oneTapSchema = v.object({
  credential: v.pipe(v.string(), v.minLength(1)),
})
